import { useEffect, useMemo } from "react";
import { BillingPopupEvent, RequestPaymentEvent } from "../../utils/bridges";
import ReceiptV2 from "../receipt-v2";
import {
  createNewReceipt,
  createReceiptRaw,
  sendPaymentLink,
  settleDues,
  viewAllReceipt,
} from "../../utils/receipt";
import { getBusinessPatientRelation } from "apis";
import { useReceiptStore } from "@elixir/stores";
import { getIdb } from "../../utils/bridges/methods";
import { refreshAppts } from "../../utils/exposed-functions/refetch-appt";
import getB from "../../utils/bridges/EkaBridge";
import { convertOfflineAppointmentToOnlineAptFormat } from "../../apis/tryst/get-all-queues";

const StethoReceiptPopup = (
  data: BillingPopupEvent["data"] | RequestPaymentEvent["data"]
) => {
  if (data.aid) {
    return <ReceiptWithAptContext {...data} />;
  }

  return <ReceiptWithoutAptContext {...data} />;
};

function ReceiptWithAptContext({
  aid,
  clinicId,
  source,
  aptDate,
  appointment,
}: BillingPopupEvent["data"] | RequestPaymentEvent["data"]) {
  const today = useMemo(() => {
    const todayDateObj = new Date();
    return todayDateObj.toISOString();
  }, []);

  useEffect(() => {
    async function setupReceiptPopup() {
      let appointmentForReceipt = appointment;
      if (!appointmentForReceipt && aid) {
        await refreshAppts({ clinicId });
        const t = (await getIdb()?.appointments?.get(aid));
        if (t) {
          appointmentForReceipt = (await convertOfflineAppointmentToOnlineAptFormat([t]))?.[0];
        }
      }

      if (appointmentForReceipt) {
        if (source === "apt-queue-view-receipts" || source === "prescription") {
          if (!!appointmentForReceipt.receipts.length) {
            viewAllReceipt({ appointment: appointmentForReceipt });
          } else {
            createNewReceipt({ appointment: appointmentForReceipt });
          }
        } else if (source === "settle-due") {
          settleDues({ appointment: appointmentForReceipt });
        } else if (source === "request-payment") {
          sendPaymentLink({ appointment: appointmentForReceipt });
        } else {
          createNewReceipt({ appointment: appointmentForReceipt });
        }
      } else {
        getB().track("ERROR", { on: "setupReceiptPopup", what: "apt not found", aid: aid || "_WTP" });
      }
    }

    setupReceiptPopup();
  }, []);

  return <ReceiptV2 />;
}

function ReceiptWithoutAptContext({
  patient,
  uhid,
  clinicId,
  serviceInfo,
  docid,
  createNewAppt,
}: BillingPopupEvent["data"] | RequestPaymentEvent["data"]) {
  const setAmountSettled = useReceiptStore((state) => state.setAmountSettled);
  const setPatientOwes = useReceiptStore((state) => state.setPatientOwes);

  useEffect(() => {
    createReceiptRaw({
      patient,
      uhid,
      clinicId,
      serviceInfo,
      doctor_oid: docid,
      createNewAppt,
    });

    async function getAndSetPendingDues() {
      const { data: patientRelationData } = await getBusinessPatientRelation({
        patientOid: patient.oid,
      });
      if (patientRelationData) {
        setPatientOwes(patientRelationData.amount_pending || 0);
        setAmountSettled(patientRelationData.amount_pending || 0);
      }
    }

    getAndSetPendingDues();
  }, []);
  return <ReceiptV2 />;
}

export default StethoReceiptPopup;
